import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './References.module.css';

const References = ({ references = [] }) => {
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [logoLoaded, setLogoLoaded] = useState(Array(references.length).fill(false));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAll, setShowAll] = useState(false);
  const cardRefs = useRef([]);

  // Track viewport changes
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Animate cards on enter
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = cardRefs.current.indexOf(entry.target);
            if (idx !== -1) {
              setVisibleCards(v => new Set(v).add(idx));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    cardRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [showAll, isMobile]);

  // Preload remaining logos, then show all
  const handleShowAll = () => {
    setShowAll(true);
    references.slice(8).forEach(ref => {
      const img = new Image();
      img.src = ref.logo;
    });
  };

  // Mark logo as loaded
  const handleImageLoad = idx => {
    setLogoLoaded(prev => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });
  };

  if (!references.length) return null;

  // Show either first 8 (mobile) or all
  const displayed = isMobile && !showAll
    ? references.slice(0, 8)
    : references;

  return (
    <section className={styles.referencesSection}>
      <h2 className={styles.title}>REFERANSLARIMIZ</h2>
      <div className={styles.grid}>
        {displayed.map((ref, idx) => (
          <div
            key={ref.id}
            ref={el => (cardRefs.current[idx] = el)}
            className={`${styles.card} ${visibleCards.has(idx) ? styles.visible : ''}`}
            style={{ animationDelay: `${idx * 0.1}s` }}
            onClick={() => setSelectedLogo(ref)}
          >
            <div className={styles.logoContainer}>
              {!logoLoaded[idx] && <div className={styles.logoSkeleton} />}
              <img
                src={ref.logo}
                alt={ref.name}
                className={styles.logo}
                loading={showAll || !isMobile ? 'eager' : 'lazy'}
                style={{ display: logoLoaded[idx] ? 'block' : 'none' }}
                onLoad={() => handleImageLoad(idx)}
              />
            </div>
          </div>
        ))}
      </div>

      {isMobile && !showAll && references.length > 8 && (
        <button className={styles.showAllBtn} onClick={handleShowAll}>
          Tüm Referansları Göster
        </button>
      )}

      {selectedLogo && (
        <div className={`${styles.modal} ${styles.active}`} onClick={() => setSelectedLogo(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button
              className={styles.closeBtn}
              onClick={() => setSelectedLogo(null)}
              aria-label="Close modal"
            >
              ×
            </button>
            <div className={styles.modalImageContainer}>
              <img
                src={selectedLogo.logo}
                alt={selectedLogo.name}
                className={styles.modalLogo}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

References.propTypes = {
  references: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      logo: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

export default References;
